import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, Download, Printer } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { fetchSignedUrl } from "../../../../lib/fileQueries";

/**
 * Master v5 Prompt 11 — the file-review Room renderer. Mounted only
 * when a File has a real linked task (interaction_instance/attempt/
 * session) and the backend's `_resolve_room_launch_view` has already
 * reauthorized BOTH the file and that linked task — this component
 * fetches its own fresh signed URL on mount, never a cached one.
 */
export default function FileRoomRenderer({ workspaceId, fasId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const frameRef = useRef(null);

  useEffect(() => {
    fetchSignedUrl(workspaceId, fasId).then(setData).catch((e) => setError(e.body?.detail || e.message));
  }, [workspaceId, fasId]);

  if (error) {
    return (
      <div className="flex items-center gap-2 text-red-600 text-sm" data-testid="file-room-error">
        <AlertTriangle size={16} /> {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex items-center gap-2 text-ink-soft" data-testid="file-room-loading">
        <Loader2 size={16} className="animate-spin" /> Loading file…
      </div>
    );
  }

  const isVideo = data.provider === "bunny_stream";
  const isDocxPptx = ["docx", "pptx"].includes(data.extension);

  return (
    <div className="space-y-3" data-testid="file-room-renderer">
      <div className="flex items-center justify-between">
        <p className="font-serif text-lg text-ink truncate">{data.original_filename}</p>
        <div className="flex items-center gap-2">
          {data.can_download && (
            <a href={data.signed_url} download={data.original_filename} data-testid="file-room-download">
              <Button size="sm" variant="outline"><Download size={13} className="mr-1.5" /> Download</Button>
            </a>
          )}
          {!isVideo && !isDocxPptx && (
            <Button size="sm" variant="outline" onClick={() => frameRef.current?.contentWindow?.print()} data-testid="file-room-print">
              <Printer size={13} className="mr-1.5" /> Print
            </Button>
          )}
        </div>
      </div>
      <div className="bg-ink/5 rounded-lg overflow-hidden" style={{ height: "70vh" }}>
        {isDocxPptx ? (
          <div className="flex items-center justify-center h-full text-ink-soft text-sm text-center px-6" data-testid="file-room-docx-honest-error">
            A preview isn't available for Word/PowerPoint files yet — download the original to view it.
          </div>
        ) : isVideo ? (
          <iframe src={data.embed_url} title="Video" className="w-full h-full border-0" allow="autoplay; fullscreen" data-testid="file-room-video-frame" />
        ) : (
          <iframe ref={frameRef} src={data.signed_url} title="Document" className="w-full h-full border-0" data-testid="file-room-pdf-frame" />
        )}
      </div>
    </div>
  );
}
